/**
 * Client-side runtime locale swap.
 *
 * The widget SSRs with one locale (resolved by middleware from cookie /
 * Accept-Language / config). After mount, the visitor's saved profile
 * preference, or a postMessage from the dashboard, can override that
 * choice WITHOUT a reload — `<RuntimeIntlProvider>` subscribes to this
 * module's signal, dynamically imports the matching messages bundle,
 * and re-renders `NextIntlClientProvider` with the new `locale` +
 * `messages` props so every `useTranslations()` call flips on next
 * render.
 *
 * Keeping the bus as a plain module avoids needing context plumbing
 * just to nudge the provider from a deep child (the ProfileTab fires
 * `setRuntimeLocale()` right after its mutation succeeds).
 */

import { DEFAULT_LOCALE, isSupportedLocale, type SupportedLocale } from "./locales";

export type MessagesBundle = Record<string, unknown>;

type Listener = (locale: SupportedLocale) => void;

let current: SupportedLocale | null = null;
const listeners = new Set<Listener>();

/** Current runtime locale, if anyone has set one. `null` means the
 *  SSR-resolved locale is still in effect — fall back to that. */
export function getRuntimeLocale(): SupportedLocale | null {
  return current;
}

/** Publish a new locale. Noop when the value matches the current one or
 *  isn't in the supported set. */
export function setRuntimeLocale(value: unknown): void {
  if (!isSupportedLocale(value)) return;
  if (current === value) return;
  current = value;
  for (const fn of listeners) fn(value);
}

type ResetListener = () => void;
const resetListeners = new Set<ResetListener>();

/**
 * Take a pick back (a language the server permanently rejected). With a locale it is a normal
 * pick; with `null` the pick is cleared and every embed falls back to the locale its host resolved
 * (SSR / `locale=` / strategy) instead of keeping the rejected one.
 */
export function resetRuntimeLocale(value: SupportedLocale | null): void {
  if (value) {
    setRuntimeLocale(value);
    return;
  }
  const had = current !== null;
  current = null;
  if (had) for (const fn of resetListeners) fn();
}

export function subscribeRuntimeLocaleReset(fn: ResetListener): () => void {
  resetListeners.add(fn);
  return () => {
    resetListeners.delete(fn);
  };
}

export function subscribeRuntimeLocale(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// Static import map — keeps every supported locale in the same chunk
// graph so the dynamic `import()` resolves predictably under Turbopack
// + production builds (a literal `import(\`@/messages/${l}.json\`) would
// be inferred as an any-string load and ship every JSON file in the repo).
const loaders: Record<SupportedLocale, () => Promise<MessagesBundle>> = {
  en: () => import("@/messages/en.json").then((m) => m.default as MessagesBundle),
  "en-US": () => import("@/messages/en-US.json").then((m) => m.default as MessagesBundle),
  "en-GB": () => import("@/messages/en-GB.json").then((m) => m.default as MessagesBundle),
  uk: () => import("@/messages/uk.json").then((m) => m.default as MessagesBundle),
  de: () => import("@/messages/de.json").then((m) => m.default as MessagesBundle),
  es: () => import("@/messages/es.json").then((m) => m.default as MessagesBundle),
  "es-MX": () => import("@/messages/es-MX.json").then((m) => m.default as MessagesBundle),
  fr: () => import("@/messages/fr.json").then((m) => m.default as MessagesBundle),
  it: () => import("@/messages/it.json").then((m) => m.default as MessagesBundle),
  nl: () => import("@/messages/nl.json").then((m) => m.default as MessagesBundle),
  pt: () => import("@/messages/pt.json").then((m) => m.default as MessagesBundle),
  "pt-BR": () => import("@/messages/pt-BR.json").then((m) => m.default as MessagesBundle),
  sv: () => import("@/messages/sv.json").then((m) => m.default as MessagesBundle),
  da: () => import("@/messages/da.json").then((m) => m.default as MessagesBundle),
  no: () => import("@/messages/no.json").then((m) => m.default as MessagesBundle),
  fi: () => import("@/messages/fi.json").then((m) => m.default as MessagesBundle),
  pl: () => import("@/messages/pl.json").then((m) => m.default as MessagesBundle),
  cs: () => import("@/messages/cs.json").then((m) => m.default as MessagesBundle),
  sk: () => import("@/messages/sk.json").then((m) => m.default as MessagesBundle),
  tr: () => import("@/messages/tr.json").then((m) => m.default as MessagesBundle),
  ro: () => import("@/messages/ro.json").then((m) => m.default as MessagesBundle),
  hu: () => import("@/messages/hu.json").then((m) => m.default as MessagesBundle),
  el: () => import("@/messages/el.json").then((m) => m.default as MessagesBundle),
  ar: () => import("@/messages/ar.json").then((m) => m.default as MessagesBundle),
  he: () => import("@/messages/he.json").then((m) => m.default as MessagesBundle),
  hi: () => import("@/messages/hi.json").then((m) => m.default as MessagesBundle),
  th: () => import("@/messages/th.json").then((m) => m.default as MessagesBundle),
  vi: () => import("@/messages/vi.json").then((m) => m.default as MessagesBundle),
  id: () => import("@/messages/id.json").then((m) => m.default as MessagesBundle),
  ja: () => import("@/messages/ja.json").then((m) => m.default as MessagesBundle),
  ko: () => import("@/messages/ko.json").then((m) => m.default as MessagesBundle),
  zh: () => import("@/messages/zh.json").then((m) => m.default as MessagesBundle),
  "zh-TW": () => import("@/messages/zh-TW.json").then((m) => m.default as MessagesBundle),
};

export async function loadMessages(locale: SupportedLocale): Promise<MessagesBundle> {
  const loader = loaders[locale] ?? loaders[DEFAULT_LOCALE];
  return loader();
}
